/*
 * PhoneHub mobile fixes: runtime patches for theme.js (load with defer AFTER theme.js).
 * Each patch is feature-detected and wrapped in try/catch. If a theme update renames
 * or removes something, that patch does nothing and the rest of the store keeps working.
 *
 *  1. Modals/drawers (ModalComponent.prototype):
 *     - set [active] on the next frames (rAF) instead of after a 100ms setTimeout
 *     - remove the body scroll-lock classes as soon as hide() starts (when no other locking modal is open)
 *     - fallback timer so after-show/after-hide always complete if transitionend never fires
 *  2. ProductForm (patched per form instance, because these methods are instance fields):
 *     - add-to-cart error: no cart drawer; error shown inline (or as a toast if the form has no error slot)
 *     - network failure: "Couldn't add to cart — please try again." instead of console.log
 *     - alert() fallback replaced by an inline message / toast
 *     - success: render the drawer from the /cart/add sections and open it straight away; cart.js is fetched in parallel
 *     - quick view: open the cart drawer while quick view closes (no waiting for it to finish)
 *  3. MotionElement: no waiting for images; durations capped at 0.3s; no scale(0) (0.95 + opacity);
 *     product grids (collection/search/recommendations/predictive search/quick view) show immediately.
 *     GridList: same grid exemption; elsewhere 0.3s with a short stagger.
 *  4. ColorSwatch (product cards): on touch devices also responds to pointerdown.
 *  5. TabsComponent: panel swap + 150ms fade-in instead of 300ms out + 300ms in.
 *  6. PageTransition: if <page-transition> was removed from the layout, still add body.page-loaded and fire page:loaded.
 *  (AccordionDetails left as is: its durations are already 0.15s/0.25s.)
 */
(function () {
  'use strict';
  if (!window.FoxTheme || !window.customElements) return;

  var EASE_OUT = [0.23, 1, 0.32, 1];
  var reduced = function () {
    return !!(FoxTheme.config && FoxTheme.config.motionReduced);
  };
  var safe = function (name, fn) {
    try {
      fn();
    } catch (e) {
      if (window.console) console.warn('[ph-mobile-fixes] ' + name + ' skipped:', e);
    }
  };

  /* Longest transition (duration + delay) on an element, in ms. */
  function transitionMs(el) {
    try {
      var cs = window.getComputedStyle(el);
      var toMs = function (v) {
        return v.split(',').map(function (s) {
          s = s.trim();
          return s.indexOf('ms') > -1 ? parseFloat(s) : parseFloat(s) * 1000;
        });
      };
      var d = toMs(cs.transitionDuration || '0s');
      var dl = toMs(cs.transitionDelay || '0s');
      var max = 0;
      for (var i = 0; i < d.length; i++) max = Math.max(max, (d[i] || 0) + (dl[i % dl.length] || 0));
      return max;
    } catch (e) {
      return 0;
    }
  }

  /* Resolves on the overlay's transitionend, or after a fallback timeout (at least 450ms). */
  function waitForOverlay(modal) {
    var overlay = modal.overlay;
    return new Promise(function (resolve) {
      var done = false;
      var timer;
      var onEnd = function (event) {
        if (event.target === overlay) finish();
      };
      var finish = function () {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (overlay) overlay.removeEventListener('transitionend', onEnd);
        resolve();
      };
      if (overlay) overlay.addEventListener('transitionend', onEnd);
      timer = setTimeout(finish, Math.max(450, (overlay ? transitionMs(overlay) : 0) + 100));
    });
  }

  /* ---------------------------------------------------------------- 1. Modals / drawers */
  safe('modal', function () {
    var Modal = customElements.get('modal-component');
    if (!Modal || !Modal.prototype) return;
    var P = Modal.prototype;

    if (typeof P.handleShowTransition === 'function') {
      P.handleShowTransition = function () {
        var self = this;
        var promise = waitForOverlay(this);
        // Two frames so the un-hidden state is painted before [active] starts the transition.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (self.open) self.setAttribute('active', '');
          });
        });
        return promise;
      };
    }

    if (typeof P.handleHideTransition === 'function') {
      P.handleHideTransition = function () {
        this.removeAttribute('active');
        return waitForOverlay(this);
      };
    }

    if (typeof P.hide === 'function') {
      var origHide = P.hide;
      P.hide = function () {
        try {
          if (this.open && this.isLockingNeeded) {
            var self = this;
            var othersOpen = Array.prototype.some.call(document.querySelectorAll('[open]'), function (el) {
              return el !== self && el instanceof Modal && el.open && el.isLockingNeeded;
            });
            if (!othersOpen) {
              var cls = this.classes || {};
              document.body.classList.remove(cls.show || 'modal-show', cls.showing || 'modal-showing');
            }
          }
        } catch (e) {}
        return origHide.apply(this, arguments);
      };
    }
  });

  /* ---------------------------------------------------------------- 2. ProductForm */
  var ADD_ERROR = 'Couldn’t add to cart — please try again.';

  function showToast(message) {
    var toast = document.getElementById('PhCartToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'PhCartToast';
      toast.setAttribute('role', 'alert');
      toast.style.cssText =
        'position:fixed;left:16px;right:16px;bottom:calc(88px + env(safe-area-inset-bottom));z-index:2147483000;' +
        'margin:0 auto;max-width:420px;padding:12px 16px;border-radius:10px;background:#1e2a3a;color:#fff;' +
        'font-size:14px;line-height:1.4;text-align:center;box-shadow:0 6px 20px rgba(0,0,0,.2);' +
        'opacity:0;transform:translateY(8px);transition:opacity 200ms cubic-bezier(0.23,1,0.32,1),transform 200ms cubic-bezier(0.23,1,0.32,1);pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(function () {
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    });
    clearTimeout(toast._phTimer);
    toast._phTimer = setTimeout(function () {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(8px)';
    }, 4000);
  }

  function hideToast() {
    var toast = document.getElementById('PhCartToast');
    if (toast) {
      clearTimeout(toast._phTimer);
      toast.style.opacity = '0';
    }
  }

  /* Re-renders the cart drawer from the /cart/add `sections` payload. Returns true on success. */
  function prerenderDrawer(sections) {
    try {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || !sections) return false;
      var id = drawer.getAttribute('data-section-id');
      if (!id || !sections[id]) return false;
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      var body = document.getElementById('CartDrawerBody-' + id);
      var footer = document.getElementById('CartDrawerFooter-' + id);
      var empty = document.getElementById('CartDrawerEmpty-' + id);
      var newBody = doc.getElementById('CartDrawerBody-' + id);
      var newFooter = doc.getElementById('CartDrawerFooter-' + id);
      var newEmpty = doc.getElementById('CartDrawerEmpty-' + id);
      if (!body || !footer || !newBody || !newFooter) return false;
      body.innerHTML = newBody.innerHTML;
      footer.innerHTML = newFooter.innerHTML;
      if (empty && newEmpty) empty.innerHTML = newEmpty.innerHTML;
      // A successful add means the cart is not empty.
      body.classList.remove('hidden');
      footer.classList.remove('hidden');
      if (empty) empty.classList.add('hidden');
      return true;
    } catch (e) {
      return false;
    }
  }

  function patchProductForm(form) {
    if (!form || form._phPatched) return;
    if (typeof form.handleFormSubmission !== 'function') return;
    form._phPatched = true;

    // Inline error, or a toast when the form has no error slot / hides errors. Never alert().
    form.displayFormErrors = function (errorMessage) {
      if (errorMessage === undefined) errorMessage = false;
      var slot = form.formErrorMessage || form.querySelector('.product-form__error-message');
      form.formErrorMessage = slot;
      if (errorMessage === false) {
        if (slot) slot.toggleAttribute('hidden', true);
        hideToast();
        return;
      }
      if (slot && !form.isHideErrors) {
        slot.toggleAttribute('hidden', false);
        slot.innerText = errorMessage;
      } else {
        showToast(errorMessage);
      }
    };

    form.showCartDrawer = function () {
      var drawer = form.cartDrawerElement;
      var quickView = form.closest('quick-view-modal');
      if (quickView) {
        if (drawer && !drawer.open) {
          // Close quick view and open the cart together.
          quickView.activeElement = null;
          var afterHide = quickView.events && quickView.events.handleAfterHide;
          if (afterHide) {
            var onHidden = function (event) {
              if (event.target !== quickView) return;
              document.body.removeEventListener(afterHide, onHidden);
              // Quick view's after-hide clears the shared focus trap; restore it for the open cart.
              setTimeout(function () {
                if (drawer.open && FoxTheme.a11y) FoxTheme.a11y.trapFocus(drawer, drawer.focusElement);
              });
            };
            document.body.addEventListener(afterHide, onHidden);
          }
          quickView.hide(true);
          drawer.show(form.lastSubmittedElement);
        } else {
          quickView.hide(true);
        }
      } else if (drawer) {
        drawer.show(form.lastSubmittedElement);
      }
    };

    form.handleFormSubmission = function (config) {
      var button = form.submitButtonElement;
      var resetButton = function () {
        if (!button) return;
        button.classList.remove('btn--loading');
        button.removeAttribute('aria-disabled');
      };
      var getCart = function () {
        return fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig('json', 'GET')).then(function (r) {
          return r.json();
        });
      };

      fetch(FoxTheme.routes.cart_add_url, config)
        .then(function (response) {
          return response.json();
        })
        .then(function (parsedState) {
          if (parsedState.status) {
            // Error: show it, keep the drawer closed, refresh counts quietly.
            form.handleCartError(parsedState);
            resetButton();
            return getCart()
              .then(function (cartJson) {
                form.updateCartState(cartJson);
              })
              .catch(function () {});
          }

          var cartPromise = getCart();
          var opened = prerenderDrawer(parsedState.sections);
          if (opened) form.showCartDrawer();

          return cartPromise
            .then(function (cartJson) {
              cartJson.sections = parsedState.sections;
              form.updateCartState(cartJson);
            })
            .catch(function () {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
            })
            .then(function () {
              form.dispatchProductAddedEvent(parsedState);
              if (!opened) form.showCartDrawer();
            });
        })
        .catch(function (error) {
          console.error(error);
          form.hasError = true;
          form.displayFormErrors(ADD_ERROR);
          try {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: form.formData && form.formData.get('id'),
              errors: ADD_ERROR,
              message: ADD_ERROR,
            });
          } catch (e) {}
        })
        .finally(resetButton);
    };
  }

  safe('product-form', function () {
    if (!customElements.get('product-form')) return;
    var patchAll = function () {
      document.querySelectorAll('form[is="product-form"]').forEach(function (form) {
        safe('product-form instance', function () {
          patchProductForm(form);
        });
      });
    };
    patchAll();
    document.addEventListener('DOMContentLoaded', patchAll);
    // Capture phase runs before the form's own submit handler, so forms added later
    // (quick view, sticky bar, section re-renders) get patched before they submit.
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (form && form.matches && form.matches('form[is="product-form"]')) {
          safe('product-form instance', function () {
            patchProductForm(form);
          });
        }
      },
      true
    );
  });

  /* ---------------------------------------------------------------- 3. MotionElement / GridList */
  var GRID_CONTEXT =
    '#ProductGridContainer, #ProductsList, product-recommendations, predictive-search, cart-drawer-products-recommendation, quick-view-modal';
  var inGrid = function (el) {
    try {
      return !!el.closest(GRID_CONTEXT);
    } catch (e) {
      return false;
    }
  };
  var clearMotionStyles = function (el) {
    try {
      if (el.getAnimations) {
        el.getAnimations().forEach(function (a) {
          a.cancel();
        });
      }
    } catch (e) {}
    el.style.visibility = '';
    el.style.opacity = '';
    el.style.transform = '';
  };
  var cap = function (seconds) {
    return Math.min(seconds || 0, 0.3);
  };

  safe('motion-element', function () {
    var Motion = customElements.get('motion-element');
    if (!Motion || !FoxTheme.Motion || typeof FoxTheme.Motion.animate !== 'function') return;
    var P = Motion.prototype;

    var markInitialized = function (el) {
      el.setAttribute('data-initialized', 'true');
      el.dispatchEvent(new CustomEvent('motion-initialized', { bubbles: false }));
    };

    if (typeof P.preInitialize === 'function') {
      var origPre = P.preInitialize;
      P.preInitialize = function () {
        if (this.isHold) return;
        if (inGrid(this)) return; // product grids are never hidden
        if (this.animationType === 'zoom-in-lg') {
          this._abortController = new AbortController();
          FoxTheme.Motion.animate(this, { transform: 'scale(0.95)', opacity: 0.01 }, { duration: 0 });
          return;
        }
        return origPre.apply(this, arguments);
      };
    }

    if (typeof P.setupObserver === 'function' && FoxTheme.MotionObserver) {
      P.setupObserver = function () {
        var self = this;
        if (inGrid(this)) {
          if (!this.isHold) {
            clearMotionStyles(this);
            markInitialized(this);
          }
          return;
        }
        this._isObserving = true;
        // Don't wait for images: reveal as soon as the element is in view.
        FoxTheme.MotionObserver.observe(this, function () {
          Promise.resolve()
            .then(function () {
              return self.initialize();
            })
            .catch(function () {});
        });
      };
    }

    if (typeof P.initialize === 'function') {
      P.initialize = async function () {
        if (this.isHold || !this.isConnected) return;
        this.setAttribute('data-initialized', 'true');
        this.dispatchEvent(new CustomEvent('motion-initialized', { bubbles: false }));
        var opts = { duration: reduced() ? 0 : 0.3, delay: cap(this.animationDelay), easing: EASE_OUT };
        var keyframes;
        switch (this.animationType) {
          case 'fade-in':
            this.style.visibility = 'visible';
            keyframes = { opacity: 1 };
            break;
          case 'fade-up':
            this.style.visibility = 'visible';
            keyframes = { transform: 'translateY(0)', opacity: 1 };
            break;
          case 'zoom-in-lg':
            keyframes = { transform: 'scale(1)', opacity: 1 };
            break;
          case 'zoom-in':
          case 'zoom-out':
          case 'zoom-out-sm':
            keyframes = { transform: 'scale(1)' };
            break;
          default:
            return;
        }
        try {
          await FoxTheme.Motion.animate(this, keyframes, opts).finished;
        } catch (e) {}
      };
    }

    if (typeof P.resetAnimation === 'function') {
      P.resetAnimation = async function (duration) {
        if (!this.isConnected) return;
        var opts = {
          duration: duration ? cap(duration) : 0.2,
          delay: cap(this.animationDelay),
          easing: duration ? 'linear' : EASE_OUT,
        };
        var keyframes;
        switch (this.animationType) {
          case 'fade-in':
            keyframes = { opacity: 0 };
            break;
          case 'fade-up':
            keyframes = { transform: 'translateY(2.5rem)', opacity: 0 };
            break;
          case 'zoom-in':
          case 'zoom-in-lg':
          case 'zoom-out':
          case 'zoom-out-sm':
            keyframes = { transform: 'scale(0.95)', opacity: 0 };
            break;
          default:
            return;
        }
        try {
          await FoxTheme.Motion.animate(this, keyframes, opts).finished;
        } catch (e) {}
      };
    }

    // Elements upgraded before this file ran: un-hide the ones in product grids, and
    // re-register the others so they no longer wait for images.
    var fixExisting = function () {
      document.querySelectorAll('motion-element').forEach(function (el) {
        if (el.hasAttribute('hold') || el.hasAttribute('data-initialized')) return;
        if (inGrid(el)) {
          try {
            if (FoxTheme.MotionObserver) FoxTheme.MotionObserver.unobserve(el);
            el._isObserving = false;
          } catch (e) {}
          clearMotionStyles(el);
          markInitialized(el);
        } else if (el._isObserving && typeof el.setupObserver === 'function') {
          el.setupObserver();
        }
      });
    };
    fixExisting();
    document.addEventListener('DOMContentLoaded', fixExisting);
  });

  safe('grid-list', function () {
    var Grid = customElements.get('grid-list');
    if (!Grid || !FoxTheme.Motion) return;
    var P = Grid.prototype;
    var reveal = function (items) {
      Array.prototype.forEach.call(items || [], clearMotionStyles);
    };

    if (typeof P.hideGridItems === 'function') {
      var origHide = P.hideGridItems;
      P.hideGridItems = function () {
        if (inGrid(this)) return;
        return origHide.apply(this, arguments);
      };
    }

    var show = function (items) {
      if (!items || !items.length) return;
      FoxTheme.Motion.animate(
        items,
        { transform: ['translateY(1rem)', 'translateY(0)'], opacity: [0.01, 1], visibility: ['hidden', 'visible'] },
        {
          duration: reduced() ? 0 : 0.3,
          delay: reduced() || typeof FoxTheme.Motion.stagger !== 'function' ? 0 : FoxTheme.Motion.stagger(0.04),
          easing: EASE_OUT,
        }
      );
    };
    if (typeof P.showGridItems === 'function') {
      P.showGridItems = function () {
        if (inGrid(this)) return reveal(this.gridItems);
        show(this.gridItems);
      };
    }
    if (typeof P.reShowVisibleGridItems === 'function') {
      P.reShowVisibleGridItems = function () {
        if (inGrid(this)) return reveal(this.visibleGridItems);
        show(this.visibleGridItems);
      };
    }

    // Existing grid-lists inside product grids: reveal now. Their original in-view callback was
    // bound before this patch, so give that instance an empty item list to keep it from re-hiding cards.
    document.querySelectorAll('grid-list').forEach(function (el) {
      if (!inGrid(el)) return;
      var items = el.gridItems;
      reveal(items);
      try {
        var none = document.querySelectorAll('.ph-no-grid-items-x');
        Object.defineProperty(el, 'gridItems', { configurable: true, get: function () { return none; } });
        Object.defineProperty(el, 'visibleGridItems', { configurable: true, get: function () { return none; } });
      } catch (e) {}
    });
  });

  /* ---------------------------------------------------------------- 4. ColorSwatch on touch */
  safe('color-swatch', function () {
    var Swatch = customElements.get('color-swatch');
    if (!Swatch || !(FoxTheme.config && FoxTheme.config.isTouch)) return;
    var P = Swatch.prototype;
    if (typeof P.onMouseEnter !== 'function') return;

    var addTouch = function (list) {
      if (!list || list._phTouch || !list.optionNodes) return;
      list._phTouch = true;
      var signal = list.abortController ? list.abortController.signal : undefined;
      list.optionNodes.forEach(function (item) {
        item.addEventListener(
          'pointerdown',
          function () {
            list.onMouseEnter({ target: item });
          },
          { passive: true, signal: signal }
        );
      });
    };

    if (typeof P.init === 'function') {
      var origInit = P.init;
      P.init = function () {
        var result = origInit.apply(this, arguments);
        this._phTouch = false;
        addTouch(this);
        return result;
      };
    }
    document.querySelectorAll('ul[is="color-swatch"]').forEach(addTouch);
  });

  /* ---------------------------------------------------------------- 5. TabsComponent */
  safe('tabs-component', function () {
    var Tabs = customElements.get('tabs-component');
    if (!Tabs || typeof Tabs.prototype.transition !== 'function' || !FoxTheme.Motion) return;
    var origTransition = Tabs.prototype.transition;
    Tabs.prototype.transition = async function (fromPanel, toPanel) {
      if (!fromPanel || !toPanel) return origTransition.apply(this, arguments);
      fromPanel.hidden = true;
      toPanel.hidden = false;
      toPanel.style.transform = '';
      try {
        FoxTheme.Motion.animate(
          toPanel,
          { opacity: [0, 1] },
          { duration: reduced() ? 0 : 0.15, easing: EASE_OUT }
        );
      } catch (e) {}
      var grid = toPanel.querySelector('grid-list');
      if (grid && typeof grid.showGridItems === 'function') grid.showGridItems();
    };
  });

  /* ---------------------------------------------------------------- 6. PageTransition removed from layout */
  safe('page-transition', function () {
    var shim = function () {
      if (document.querySelector('page-transition')) return; // the theme handles it
      if (document.body.classList.contains('page-loaded')) return;
      document.body.classList.add('page-loaded');
      document.dispatchEvent(new CustomEvent('page:loaded'));
    };
    // Run after DOMContentLoaded so listeners added by later deferred scripts still receive page:loaded.
    if (document.readyState === 'complete') {
      shim();
    } else {
      document.addEventListener('DOMContentLoaded', shim);
      window.addEventListener('load', shim);
    }
  });
})();
